import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import toast, { Toaster } from "react-hot-toast";
import { FiShield, FiSearch, FiX, FiEdit2, FiPlus, FiTrash2, FiSave, FiGlobe, FiUsers } from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const Toggle = ({ checked, onClick, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-60 ${
      checked ? "bg-[#ffff00]" : "bg-gray-200"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

const emptyOverrideForm = { stepId: null, countryCode: "", isActive: true, isRequired: true, isNew: true };

const KYC = () => {
  const token = localStorage.getItem("token");

  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [countryCodeFilter, setCountryCodeFilter] = useState("");
  const [togglingKey, setTogglingKey] = useState(null);

  const [editingStep, setEditingStep] = useState(null);
  const [savingStep, setSavingStep] = useState(false);

  const [overrideForm, setOverrideForm] = useState(emptyOverrideForm);
  const [savingOverride, setSavingOverride] = useState(false);
  const [deletingOverrideKey, setDeletingOverrideKey] = useState(null);

  const fetchSteps = useCallback(async (code) => {
    setLoading(true);
    setError("");
    try {
      const query = code ? `?countryCode=${encodeURIComponent(code)}` : "";
      const res = await fetch(`${baseUrl}/kyc/steps${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (res.ok && result.status) {
        setSteps(result.data || []);
      } else {
        setError(result.message || "Failed to fetch KYC steps");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchSteps(""); }, [fetchSteps]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSteps(countryCodeFilter.trim().toUpperCase());
  };

  const handleClear = () => {
    setCountryCodeFilter("");
    fetchSteps("");
  };

  const handleToggleStep = async (step, field) => {
    const key = `${step.id}-${field}`;
    setTogglingKey(key);
    try {
      const res = await fetch(`${baseUrl}/kyc/steps/${step.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: step.name,
          description: step.description,
          isActive: field === "isActive" ? !step.isActive : step.isActive,
          isRequired: field === "isRequired" ? !step.isRequired : step.isRequired,
        }),
      });
      const result = await res.json();
      if (res.ok) {
        setSteps((prev) => prev.map((s) => (s.id === step.id ? { ...s, [field]: !s[field] } : s)));
        toast.success("Step updated!");
      } else {
        toast.error(result.message || "Failed to update step");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setTogglingKey(null);
    }
  };

  const openEditStep = (step) => {
    setEditingStep({ ...step });
  };

  const closeEditStep = () => setEditingStep(null);

  const handleSaveStep = async () => {
    setSavingStep(true);
    try {
      const res = await fetch(`${baseUrl}/kyc/steps/${editingStep.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: editingStep.name,
          description: editingStep.description,
          isActive: editingStep.isActive,
          isRequired: editingStep.isRequired,
        }),
      });
      const result = await res.json();
      if (res.ok) {
        setSteps((prev) => prev.map((s) => (s.id === editingStep.id ? { ...s, ...editingStep } : s)));
        toast.success("Step updated successfully!");
        closeEditStep();
      } else {
        toast.error(result.message || "Failed to update step");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSavingStep(false);
    }
  };

  const openAddOverride = (step) => {
    setOverrideForm({ stepId: step.id, countryCode: "", isActive: true, isRequired: true, isNew: true });
  };

  const openEditOverride = (step, override) => {
    setOverrideForm({
      stepId: step.id,
      countryCode: override.countryCode,
      isActive: override.isActive,
      isRequired: override.isRequired,
      isNew: false,
    });
  };

  const closeOverrideForm = () => setOverrideForm(emptyOverrideForm);

  const handleSaveOverride = async () => {
    const code = overrideForm.countryCode.trim().toUpperCase();
    if (!code) {
      toast.error("Country code is required");
      return;
    }
    setSavingOverride(true);
    try {
      const res = await fetch(`${baseUrl}/kyc/steps/${overrideForm.stepId}/country-override`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          countryCode: code,
          isActive: overrideForm.isActive,
          isRequired: overrideForm.isRequired,
        }),
      });
      const result = await res.json();
      if (res.ok) {
        setSteps((prev) =>
          prev.map((s) => {
            if (s.id !== overrideForm.stepId) return s;
            const overrides = s.countryOverrides || [];
            const exists = overrides.some((o) => o.countryCode === code);
            const nextOverrides = exists
              ? overrides.map((o) => (o.countryCode === code ? { ...o, isActive: overrideForm.isActive, isRequired: overrideForm.isRequired } : o))
              : [...overrides, { countryCode: code, isActive: overrideForm.isActive, isRequired: overrideForm.isRequired }];
            return { ...s, countryOverrides: nextOverrides };
          })
        );
        toast.success("Country override saved!");
        closeOverrideForm();
      } else {
        toast.error(result.message || "Failed to save override");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSavingOverride(false);
    }
  };

  const handleDeleteOverride = async (step, countryCode) => {
    if (!window.confirm(`Remove the ${countryCode} override for "${step.name}"?`)) return;
    const key = `${step.id}-${countryCode}`;
    setDeletingOverrideKey(key);
    try {
      const res = await fetch(`${baseUrl}/kyc/steps/${step.id}/country-override/${countryCode}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSteps((prev) =>
          prev.map((s) =>
            s.id === step.id
              ? { ...s, countryOverrides: (s.countryOverrides || []).filter((o) => o.countryCode !== countryCode) }
              : s
          )
        );
        toast.success("Override removed!");
      } else {
        const result = await res.json();
        toast.error(result.message || "Failed to remove override");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setDeletingOverrideKey(null);
    }
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">

          {/* Heading & Sub-navigation Tabs */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#ffffe0] flex items-center justify-center">
                <FiShield size={20} className="text-[#999900]" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-gray-900 tracking-tight">KYC Steps & Rules</h1>
                <p className="text-xs text-gray-500 mt-0.5">Manage global verification requirements and country-specific overrides</p>
              </div>
            </div>

            {/* Sub-navigation Tabs */}
            <div className="flex items-center gap-1.5 bg-gray-200/80 p-1 rounded-xl">
              <Link
                to="/kyc"
                className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-[#ffff00] text-black shadow-xs flex items-center gap-1.5"
              >
                <FiShield size={13} />
                <span>KYC Steps</span>
              </Link>
              <Link
                to="/kyc-users"
                className="px-3.5 py-1.5 rounded-lg text-xs font-semibold text-gray-600 hover:text-black hover:bg-white/80 transition flex items-center gap-1.5"
              >
                <FiUsers size={13} />
                <span>KYC Users List</span>
              </Link>
            </div>
          </div>

          {/* Filter bar */}
          <form onSubmit={handleSearch} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <FiGlobe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Country code (e.g. IN)"
                value={countryCodeFilter}
                onChange={(e) => setCountryCodeFilter(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] uppercase"
              />
            </div>
            <button
              type="submit"
              className="flex items-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] text-black text-sm font-medium px-4 py-2.5 rounded-lg transition"
            >
              <FiSearch size={14} /> Search
            </button>
            {countryCodeFilter && (
              <button
                type="button"
                onClick={handleClear}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
              >
                <FiX size={14} /> Clear
              </button>
            )}
          </form>

          {/* Steps table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">KYC Steps</h3>
              {steps.length > 0 && (
                <span className="text-xs text-gray-400">{steps.length} step{steps.length !== 1 ? "s" : ""}</span>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="w-7 h-7 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : error ? (
              <div className="m-5 bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-sm">{error}</div>
            ) : steps.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <FiShield size={32} className="text-gray-300 mb-3" />
                <p className="text-sm">No KYC steps found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3">#</th>
                      <th className="px-5 py-3">Step</th>
                      <th className="px-5 py-3 text-center">Active</th>
                      <th className="px-5 py-3 text-center">Required</th>
                      <th className="px-5 py-3">Country Overrides</th>
                      <th className="px-5 py-3 text-center w-20">Edit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {steps.map((step) => (
                      <tr key={step.id} className="hover:bg-gray-50 transition align-top">
                        <td className="px-5 py-3.5 text-gray-400 text-xs">{step.stepNumber}</td>
                        <td className="px-5 py-3.5">
                          <div className="font-medium text-gray-800">{step.name}</div>
                          <div className="text-xs text-gray-400">{step.stepKey}</div>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <Toggle
                            checked={step.isActive}
                            disabled={togglingKey === `${step.id}-isActive`}
                            onClick={() => handleToggleStep(step, "isActive")}
                          />
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <Toggle
                            checked={step.isRequired}
                            disabled={togglingKey === `${step.id}-isRequired`}
                            onClick={() => handleToggleStep(step, "isRequired")}
                          />
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {(step.countryOverrides || []).map((override) => (
                              <span
                                key={override.countryCode}
                                className={`flex items-center gap-1.5 text-xs font-medium pl-2.5 pr-1.5 py-1 rounded-full ${
                                  override.isActive ? "bg-[#ffffe0] text-[#999900]" : "bg-gray-100 text-gray-500"
                                }`}
                              >
                                {override.countryCode}
                                <button
                                  onClick={() => openEditOverride(step, override)}
                                  className="hover:text-amber-600"
                                  title="Edit override"
                                >
                                  <FiEdit2 size={11} />
                                </button>
                                <button
                                  onClick={() => handleDeleteOverride(step, override.countryCode)}
                                  disabled={deletingOverrideKey === `${step.id}-${override.countryCode}`}
                                  className="hover:text-red-600 disabled:opacity-50"
                                  title="Remove override"
                                >
                                  <FiTrash2 size={11} />
                                </button>
                              </span>
                            ))}
                            <button
                              onClick={() => openAddOverride(step)}
                              className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-[#999900] hover:bg-[#ffffe0] px-2 py-1 rounded-full transition"
                            >
                              <FiPlus size={11} /> Add
                            </button>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <button
                            onClick={() => openEditStep(step)}
                            className="flex items-center gap-1 text-xs text-amber-500 hover:bg-amber-50 px-2.5 py-1.5 rounded-lg transition mx-auto"
                          >
                            <FiEdit2 size={12} /> Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Edit Step Modal */}
      {editingStep && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeEditStep}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-gray-800">Edit KYC Step</h3>
              <button onClick={closeEditStep} className="text-gray-400 hover:text-gray-600"><FiX size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                <input
                  type="text"
                  value={editingStep.name}
                  onChange={(e) => setEditingStep({ ...editingStep, name: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={editingStep.description || ""}
                  onChange={(e) => setEditingStep({ ...editingStep, description: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] resize-none"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-500">Active</label>
                <Toggle checked={editingStep.isActive} onClick={() => setEditingStep({ ...editingStep, isActive: !editingStep.isActive })} />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-500">Required</label>
                <Toggle checked={editingStep.isRequired} onClick={() => setEditingStep({ ...editingStep, isRequired: !editingStep.isRequired })} />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveStep}
                disabled={savingStep}
                className="flex-1 flex items-center justify-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-60 text-black text-sm font-medium py-2.5 rounded-lg transition"
              >
                <FiSave size={14} />
                {savingStep ? "Saving..." : "Save Changes"}
              </button>
              <button onClick={closeEditStep} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Country Override Modal */}
      {overrideForm.stepId && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeOverrideForm}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-gray-800">
                {overrideForm.isNew ? "Add Country Override" : "Edit Country Override"}
              </h3>
              <button onClick={closeOverrideForm} className="text-gray-400 hover:text-gray-600"><FiX size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Country Code</label>
                <input
                  type="text"
                  disabled={!overrideForm.isNew}
                  placeholder="e.g. IN"
                  value={overrideForm.countryCode}
                  onChange={(e) => setOverrideForm({ ...overrideForm, countryCode: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] uppercase disabled:bg-gray-50 disabled:text-gray-400"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-500">Active</label>
                <Toggle checked={overrideForm.isActive} onClick={() => setOverrideForm({ ...overrideForm, isActive: !overrideForm.isActive })} />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-500">Required</label>
                <Toggle checked={overrideForm.isRequired} onClick={() => setOverrideForm({ ...overrideForm, isRequired: !overrideForm.isRequired })} />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveOverride}
                disabled={savingOverride}
                className="flex-1 flex items-center justify-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-60 text-black text-sm font-medium py-2.5 rounded-lg transition"
              >
                <FiSave size={14} />
                {savingOverride ? "Saving..." : "Save Override"}
              </button>
              <button onClick={closeOverrideForm} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KYC;
